import React, { useCallback, useEffect, useMemo, useState, useImperativeHandle, forwardRef, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Stack, Typography, Menu, ListItemIcon, ListItemText, MenuItem, Tooltip } from "@mui/material";
import { AttachmentRounded, DragIndicatorRounded, EditRounded, MoreHorizRounded, UploadRounded, AddRounded, AddCircleRounded, DriveFileRenameOutlineRounded, WidgetsRounded, CheckRounded, LinkRounded, CloseRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Bynder, FileReplace } from "@zesty-io/material";
import { useIntersection } from "react-use";
import { useTranslation, Trans } from "react-i18next";
import { useGetBinsQuery, useGetFileQuery, useUpdateFileMutation } from "../../../../../shell/services/mediaManager";
import { fileUploadStage } from "../../../../../shell/store/media-revamp";
import { useDropzone } from "react-dropzone";
import { IconButton, ImageSync } from "@zesty-io/material";
import { FileModal } from "../../../../media/src/app/components/FileModal";
import RenameFileModal from "../../../../media/src/app/components/FileModal/RenameFileModal";
import { fileExtension } from "../../../../media/src/app/utils/fileUtils";
import { loadingThumbnailSx } from "../../../../media/src/app/components/Thumbnail/loadingStyles";
import { FileTypePreview } from "../../../../media/src/app/components/FileModal/FileTypePreview";
import { useGetInstanceSettingsQuery } from "../../../../../shell/services/instance";
import { ReplaceFileModal } from "../../../../media/src/app/components/FileModal/ReplaceFileModal";
import openBynder from "../../../../../utility/openBynder";
import { useDrag, useDrop } from "react-dnd";
import DndContextProvider from "shell/components/DndContextProvider";
type FieldTypeMediaProps = {
  images: string[];
  limit: number;
  openMediaBrowser: (opts: any) => void;
  name: string;
  onChange: (value: string, name: string) => void;
  hasError?: boolean;
  hideDrag?: boolean;
  lockedToGroupId: string | null;
  settings?: any;
  compact?: boolean;
};
export const FieldTypeMedia = forwardRef(({
  images,
  limit,
  openMediaBrowser,
  onChange,
  name,
  hasError,
  hideDrag,
  lockedToGroupId,
  settings,
  compact
}: FieldTypeMediaProps, ref) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [localImageZUIDs, setLocalImageZUIDs] = useState<string[]>(images);
  const instanceId = useSelector((state: any) => state.instance.ID);
  const ecoId = useSelector((state: any) => state.instance.ecoID);
  const {
    data: bins
  } = useGetBinsQuery({
    instanceId,
    ecoId
  });
  const defaultBin = bins?.find(bin => bin.default);
  const dispatch = useDispatch();
  const [showFileModal, setShowFileModal] = useState("");
  const [imageToReplace, setImageToReplace] = useState("");
  const {
    data: rawInstanceSettings
  } = useGetInstanceSettingsQuery();
  const [selectionError, setSelectionError] = useState("");
  // This widget also renders outside content-editor (shell Favicon /
  // FieldTypeRepeater, schema DefaultValueInput) where the lazy "content"
  // namespace isn't loaded; request it here (non-suspense so we never throw in
  // trees without a Suspense boundary — keys resolve once it loads).
  const {
    t,
    i18n
  } = useTranslation("content", {
    useSuspense: false
  });
  const bynderPortalUrlSetting = rawInstanceSettings?.find(setting => setting.key === "bynder_portal_url");
  const bynderTokenSetting = rawInstanceSettings?.find(setting => setting.key === "bynder_token");
  // Checks if the bynder portal and token are set
  const isBynderSessionValid = useMemo(() => {
    return bynderPortalUrlSetting?.value && bynderTokenSetting?.value;
  }, [bynderPortalUrlSetting, bynderTokenSetting]);
  useEffect(() => {
    setLocalImageZUIDs(images);
  }, [images]);
  useEffect(() => {
    if (bynderPortalUrlSetting?.value) {
      localStorage.setItem("cvad", bynderPortalUrlSetting.value);
    } else {
      localStorage.removeItem("cvad");
    }
  }, [bynderPortalUrlSetting]);
  useEffect(() => {
    if (bynderTokenSetting?.value) {
      localStorage.setItem("cvrt", bynderTokenSetting.value);
    } else {
      localStorage.removeItem("cvrt");
    }
  }, [bynderTokenSetting]);
  useImperativeHandle(ref, () => ({
    triggerOpenMediaBrowser() {
      openMediaBrowser({
        limit,
        callback: addZestyImage
      });
    }
  }));
  const addZestyImage = (selectedImages: any[]) => {
    const removedImages: any[] = [];
    const filteredSelectedImages = selectedImages?.filter(selectedImage => {
      //remove any images that do not match the file extension
      if (settings?.fileExtensions) {
        if (settings?.fileExtensions?.includes(`.${fileExtension(selectedImage.filename)}`)) {
          return true;
        } else {
          removedImages.push(selectedImage);
          return false;
        }
      } else {
        return true;
      }
    });
    if (removedImages.length) {
      const filenames = removedImages.map(image => image.filename);
      const formattedFilenames = new Intl.ListFormat(i18n.language, {
        type: "conjunction"
      }).format(filenames);
      setSelectionError(t("content.mediaCouldNotAdd", {
        filenames: formattedFilenames,
        errorMessage: settings?.fileExtensionsErrorMessage ?? ""
      }));
    } else {
      setSelectionError("");
    }
    const newImageZUIDs = filteredSelectedImages?.map(image => image.id);

    // remove any duplicates
    const filteredImageZUIDs = newImageZUIDs.filter(zuid => !images.includes(zuid));

    // Do not trigger onChange if no images are added
    if (![...images, ...filteredImageZUIDs]?.length) return;
    onChange([...images, ...filteredImageZUIDs].join(","), name);
  };
  const addBynderAsset = (selectedAsset: ReadonlyArray<BynderImage>) => {
    if (images.length > limit || !selectedAsset?.length) return;
    const removedAssets: any[] = [];
    const filteredBynderAssets = selectedAsset?.filter(asset => {
      if (settings?.fileExtensions) {
        const assetExtension = `.${asset.extensions[0]}`;
        if (settings?.fileExtensions?.includes(assetExtension)) {
          return true;
        } else {
          removedAssets.push(asset);
          return false;
        }
      } else {
        return true;
      }
    });
    if (removedAssets.length) {
      const filenames = removedAssets.map(asset => asset.name);
      const formattedFilenames = new Intl.ListFormat(i18n.language, {
        type: "conjunction"
      }).format(filenames);
      setSelectionError(t("content.mediaCouldNotAdd", {
        filenames: formattedFilenames,
        errorMessage: settings?.fileExtensionsErrorMessage ?? ""
      }));
    } else {
      setSelectionError("");
    }
    const newBynderAssets = filteredBynderAssets.slice(0, limit - images.length).map(asset => asset.originalUrl);
    const filteredBynderAssetsUrls = newBynderAssets.filter(asset => !images.includes(asset));
    onChange([...images, ...filteredBynderAssetsUrls].join(","), name);
  };
  const removeImage = (imageId: string) => {
    const newImageZUIDs = images.filter(image => image !== imageId);
    onChange(newImageZUIDs.join(","), name);
  };
  const replaceImage = (images: any[]) => {
    const imageZUID = images.map(image => image.id)?.[0];
    let imageToReplace: string;
    setImageToReplace((value: string) => {
      imageToReplace = value;
      return "";
    });
    // if selected replacement image is already in the list of images, do nothing
    if (localImageZUIDs.includes(imageZUID)) return;
    // if extension is not allowed set error message
    if (settings?.fileExtensions) {
      if (!settings?.fileExtensions?.includes(`.${fileExtension(images[0].filename)}`)) {
        setSelectionError(t("content.mediaCouldNotReplace", {
          errorMessage: settings?.fileExtensionsErrorMessage ?? ""
        }));
        return;
      } else {
        setSelectionError("");
      }
    }
    const newImageZUIDs = localImageZUIDs.map(zuid => {
      if (zuid === imageToReplace) {
        return imageZUID;
      }
      return zuid;
    });
    onChange(newImageZUIDs.join(","), name);
  };
  const replaceBynderAsset = (selectedAsset: BynderImage) => {
    // Prevent adding bynder asset that has already been added
    if (localImageZUIDs.includes(selectedAsset.originalUrl)) return;
    const assetExtension = `.${selectedAsset.extensions[0]}`;
    if (settings?.fileExtensions && !settings?.fileExtensions?.includes(assetExtension)) {
      setSelectionError(t("content.mediaCouldNotReplace", {
        errorMessage: settings?.fileExtensionsErrorMessage ?? ""
      }));
      return;
    } else {
      setSelectionError("");
    }
    const newImages = localImageZUIDs.map(image => {
      if (image === imageToReplace) {
        return selectedAsset.originalUrl;
      }
      return image;
    });
    setImageToReplace("");
    onChange(newImages.join(","), name);
  };
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!defaultBin) return;
    openMediaBrowser({
      limit,
      callback: addZestyImage
    });
    dispatch(fileUploadStage(acceptedFiles.map(file => {
      return {
        file,
        bin_id: defaultBin.id,
        group_id: lockedToGroupId ? lockedToGroupId : defaultBin.id
      };
    })));
  }, [defaultBin, dispatch, addZestyImage]);
  const handleReorder = () => {
    const newLocalImages = [...localImageZUIDs];
    const draggedField = newLocalImages[draggedIndex];
    newLocalImages.splice(draggedIndex, 1);
    newLocalImages.splice(hoveredIndex, 0, draggedField);
    setDraggedIndex(null);
    setHoveredIndex(null);
    setLocalImageZUIDs(newLocalImages);
    onChange(newLocalImages.join(","), name);
  };
  const handleOpenBynder = () => {
    openBynder({
      url: bynderPortalUrlSetting?.value,
      onSuccess: assets => {
        if (imageToReplace) {
          replaceBynderAsset(assets[0]);
        } else {
          addBynderAsset(assets);
        }
      },
      mode: limit > 1 && !imageToReplace ? "MultiSelect" : "SingleSelect"
    });
  };
  useEffect(() => {
    if (!!imageToReplace) {
      if (imageToReplace.includes("bynder.com")) {
        handleOpenBynder();
      } else {
        openMediaBrowser({
          callback: replaceImage,
          isReplace: true,
          onClose: () => setImageToReplace("")
        });
      }
    }
  }, [imageToReplace]);
  const sortedImages = useMemo(() => {
    if (draggedIndex === null || hoveredIndex === null) {
      return localImageZUIDs;
    } else {
      const newImages = [...localImageZUIDs];
      const draggedImage = newImages[draggedIndex];
      newImages.splice(draggedIndex, 1);
      newImages.splice(hoveredIndex, 0, draggedImage);
      return newImages;
    }
  }, [draggedIndex, hoveredIndex, localImageZUIDs]);
  const {
    getRootProps,
    getInputProps,
    open,
    isDragActive
  } = useDropzone({
    onDrop
  });
  if (!images?.length) return <Box {...getRootProps({
    onClick: evt => evt.stopPropagation(),
    onKeyDown: evt => evt.stopPropagation()
  })} sx={{
    "&:focus-visible": {
      outlineColor: theme => theme.palette.primary.main,
      borderRadius: 2
    }
  }}>
          <input {...getInputProps()} />
          <Box sx={{
      border: theme => `1px dashed ${theme.palette.primary.main}`,
      borderRadius: "8px",
      backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
      borderColor: hasError ? "error.main" : "primary.main"
    }}>
            {compact ? <Stack alignItems="center" gap={1.5} px={2} py={1.5}>
                <Typography variant="body3" color="text.secondary" fontWeight={600}>
                  {isDragActive ? t("content.mediaDropFilesHere") : t("content.mediaDragDropFilesCompact")}
                </Typography>
                {!isDragActive && <Stack direction="row" gap={2}>
                    <Button data-cy="uploadMediaButton" size="small" variant="text" onClick={open} startIcon={<UploadRounded sx={{
            width: "20px",
            height: "20px"
          }} />} sx={{
            px: 1.25
          }}>
                      {t("content.mediaUploadMedia")}
                    </Button>
                    <Button data-cy="selectFromMediaButton" size="small" variant="text" sx={{
            px: 1.25
          }} onClick={() => {
            openMediaBrowser({
              limit,
              callback: addZestyImage
            });
          }} startIcon={<AddCircleRounded sx={{
            width: "20px",
            height: "20px"
          }} />}>
                      {t("content.mediaAddFromMedia")}
                    </Button>
                  </Stack>}
              </Stack> : <Stack alignItems="center" gap={2} py={4} justifyContent="center">
                {isDragActive ? <UploadRounded color="primary" /> : <AttachmentRounded color="primary" />}
                <Typography align="center" variant="h5" color="primary" fontWeight={600}>
                  {isDragActive ? t("content.mediaDropToUpload") : <Trans i18nKey="content.mediaDragAndDropRich" components={{
            break: <br />
          }} />}
                </Typography>
                {!isDragActive && <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                    <Button size="large" variant="outlined" onClick={open} startIcon={<UploadRounded />} fullWidth sx={{
            maxWidth: "196px",
            flexShrink: 0
          }}>
                      {t("common.upload")}
                    </Button>
                    <Button data-cy="selectFromMediaButton" fullWidth size="large" startIcon={<AddRounded />} variant="outlined" onClick={() => {
            openMediaBrowser({
              limit,
              callback: addZestyImage
            });
          }} sx={{
            maxWidth: "196px",
            flexShrink: 0
          }}>
                      {t("content.mediaAddFromMedia")}
                    </Button>
                    {isBynderSessionValid && <Button data-cy="addFromBynderBtn" size="large" variant="outlined" onClick={handleOpenBynder} startIcon={<Bynder />} fullWidth sx={{
            maxWidth: "240px",
            flexShrink: 0
          }}>
                        {t("content.mediaAddFromBynder")}
                      </Button>}
                  </Box>}
              </Stack>}
          </Box>
          {selectionError && <Typography variant="body2" color="error.dark" mt={0.5}>
              {selectionError}
            </Typography>}
        </Box>;
  return <>
        <Stack data-cy="mediaItem-container" gap={1} sx={{
      border: theme => hasError ? `1px solid ${theme.palette.error.main}` : "none"
    }}>
          <DndContextProvider>
            {sortedImages.map((image, index) => {
          const isBynderAsset = image?.includes("bynder.com");
          return <MediaItem key={image} imageZUID={image} index={index} setDraggedIndex={setDraggedIndex} setHoveredIndex={setHoveredIndex} onReorder={handleReorder} onPreview={(imageZUID: string) => setShowFileModal(imageZUID)} onRemove={removeImage} onReplace={imageZUID => {
            setImageToReplace(imageZUID);
          }} hideDrag={hideDrag || limit === 1} isBynderAsset={isBynderAsset} isBynderSessionValid={!!isBynderSessionValid} compact={compact} />;
        })}
          </DndContextProvider>
          {limit > images.length && <Box display="flex" gap={1}>
              {!isBynderSessionValid && <Button size="large" variant="outlined" onClick={open} startIcon={<UploadRounded />} fullWidth>
                  {t("common.upload")}
                </Button>}
              <Button data-cy="selectFromMediaButton" size="large" variant="outlined" onClick={() => {
          openMediaBrowser({
            limit,
            callback: addZestyImage
          });
        }} fullWidth startIcon={<AddRounded />}>
                {t("content.mediaAddMoreFromMedia")}
              </Button>
              {isBynderSessionValid && <Button data-cy="addFromBynderBtn" size="large" variant="outlined" onClick={handleOpenBynder} startIcon={<Bynder />} fullWidth>
                  {t("content.mediaAddFromBynder")}
                </Button>}
            </Box>}
        </Stack>
        {selectionError && <Typography variant="body2" color="error.dark" mt={0.5}>
            {selectionError}
          </Typography>}
        {showFileModal && <FileModal fileId={showFileModal} onClose={() => setShowFileModal("")} currentFiles={sortedImages?.filter(image => typeof image === "string") as string[]} onFileChange={fileId => {
      setShowFileModal(fileId);
    }} />}
      </>;
});
type MediaItemProps = {
  imageZUID: string;
  onReorder?: () => void;
  setDraggedIndex?: (index: number) => void;
  setHoveredIndex?: (index: number) => void;
  index: number;
  onPreview?: (imageZUID: string) => void;
  onRemove?: (imageZUID: string) => void;
  onReplace?: (imageZUID: string) => void;
  hideDrag?: boolean;
  isBynderAsset: boolean;
  isBynderSessionValid: boolean;
  hideActionButtons?: boolean;
  compact?: boolean;
};
export const MediaItem = ({
  imageZUID,
  onReorder,
  setDraggedIndex,
  setHoveredIndex,
  index,
  onPreview,
  onRemove,
  onReplace,
  hideDrag,
  isBynderAsset,
  isBynderSessionValid,
  hideActionButtons,
  compact
}: MediaItemProps) => {
  // See note in FieldTypeMedia: ensure the "content" namespace is loaded even
  // when this renders outside content-editor.
  const {
    t
  } = useTranslation("content", {
    useSuspense: false
  });
  const lastHoveredIndexRef = useRef(null);
  const mediaItemContainerRef = useRef(null);
  const [isDraggable, setIsDraggable] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const effectiveHideDrag = compact || hideDrag;
  const intersection = useIntersection(mediaItemContainerRef, {
    threshold: 0
  });
  const isInView = intersection && intersection.intersectionRatio > 0;
  const {
    data,
    isFetching
  } = useGetFileQuery(imageZUID, {
    skip: imageZUID?.substr(0, 4) === "http" || !isInView
  });
  const [showRenameFileModal, setShowRenameFileModal] = useState(false);
  const [isReplaceFileModalOpen, setIsReplaceFileModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isCopiedZuid, setIsCopiedZuid] = useState(false);
  const [newFilename, setNewFilename] = useState("");
  const [updateFile, {
    reset: resetUpdate,
    isSuccess: isSuccessUpdate,
    isLoading: isLoadingUpdate
  }] = useUpdateFileMutation();
  useEffect(() => {
    if (data?.filename) {
      setNewFilename(data.filename.substring(0, data.filename.lastIndexOf(".")));
    }
  }, [data]);
  const handleCopyClick = (data: string, isZuid = false) => {
    navigator?.clipboard?.writeText(data).then(() => {
      if (isZuid) {
        setIsCopiedZuid(true);
      } else {
        setIsCopied(true);
      }
      setTimeout(() => {
        if (isZuid) {
          setIsCopiedZuid(false);
        } else {
          setIsCopied(false);
        }
      }, 5000);
    }).catch(err => {
      console.error(err);
    });
  };
  const isURL = imageZUID?.substr(0, 4) === "http";
  const handleUpdateMutation = (renamedFilename?: string) => {
    let constructedFileType = "";
    if (fileExtension(data.filename)) constructedFileType = `.${fileExtension(data.filename)}`;
    updateFile({
      id: data.id,
      previousGroupId: data.group_id,
      body: {
        group_id: data.group_id,
        filename: `${renamedFilename}${constructedFileType}`
      }
    });
  };
  const [{
    isDragging
  }, drag, preview] = useDrag({
    type: "FIELD_TYPE_MEDIA",
    canDrag: !effectiveHideDrag,
    item: () => {
      setDraggedIndex?.(index);
      return {
        index,
        imageZUID
      };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    end: () => {
      onReorder?.();
      lastHoveredIndexRef.current = null;
    }
  }, [index, imageZUID, onReorder, setDraggedIndex, effectiveHideDrag]);
  const [, drop] = useDrop({
    accept: "FIELD_TYPE_MEDIA",
    canDrop: () => !effectiveHideDrag,
    hover: (item: {
      index: number;
      imageZUID: string;
    }, monitor) => {
      if (effectiveHideDrag || !monitor.isOver({
        shallow: true
      }) || lastHoveredIndexRef.current === index) {
        return;
      }
      setHoveredIndex?.(index);
      lastHoveredIndexRef.current = index;
    }
  }, [index, setHoveredIndex, effectiveHideDrag]);
  const dragDropRef = useCallback((node: HTMLElement | null) => {
    if (effectiveHideDrag) return;
    drag(node);
    drop(node);
    preview(node);
  }, [drag, drop, preview, effectiveHideDrag]);
  return <Box ref={mediaItemContainerRef} data-sentry-element="Box" data-sentry-component="MediaItem" data-sentry-source-file="FieldTypeMedia.tsx">
      <Box ref={effectiveHideDrag ? null : dragDropRef} data-cy="mediaItem" display="grid" gridTemplateColumns={effectiveHideDrag ? "min-content 1fr" : "repeat(2, min-content) 1fr"} draggable={isDraggable} onClick={() => {
      if (isURL || !data) return;
      onPreview && onPreview(imageZUID);
    }} alignItems="center" sx={{
      border: theme => `1px solid ${theme.palette.border}`,
      borderRadius: 2,
      backgroundColor: "background.paper",
      overflow: "hidden",
      opacity: isDragging ? 0 : 1,
      transform: "translate(0, 0)"
    }} position="relative" data-sentry-element="Box" data-sentry-source-file="FieldTypeMedia.tsx">
        {isBynderAsset && <Box data-cy="bynderAssetIndicator" width={24} height={24} borderRadius={100} position="absolute" left={effectiveHideDrag ? 52 : 84} top={52} zIndex={2} px={0.25} boxSizing="border-box" sx={{
        backgroundColor: "#0af"
      }}>
            <Bynder sx={{
          width: "100%",
          color: "common.white"
        }} />
          </Box>}
        {!effectiveHideDrag && <IconButton ref={drag} disableRipple disableFocusRipple disableTouchRipple size="small" sx={{
        cursor: "grab"
      }} onMouseEnter={() => setIsDraggable(true)} onMouseLeave={() => setIsDraggable(false)} onClick={(event: any) => event.stopPropagation()}>
            <DragIndicatorRounded fontSize="small" />
          </IconButton>}
        <Box position="relative" width="80px" height="80px" bgcolor="grey.100" data-sentry-element="Box" data-sentry-source-file="FieldTypeMedia.tsx">
          {isFetching ? <Box sx={loadingThumbnailSx} data-cy="thumbnail-loading" /> : <FileTypePreview src={isURL ? imageZUID : data?.url} filename={isURL ? imageZUID : data?.filename} updatedAt={data?.updated_at} isMediaThumbnail />}
        </Box>
        <Box display="grid"
      // TODO: should there be a min width for the label?
      gridTemplateColumns={hideActionButtons ? "1fr" : "minmax(0px, auto) 112px"} alignItems="center" px={2} py={2.25} gap={2} width="100%" justifyContent={"space-between"} boxSizing="border-box" data-sentry-element="Box" data-sentry-source-file="FieldTypeMedia.tsx">
          {isURL ? <Typography variant="body2" color="text.primary" noWrap>
              {imageZUID}
            </Typography> : <Box overflow="hidden">
              <Typography variant="body2" color="text.primary" fontWeight={600} noWrap>
                {data?.filename}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {data?.title}
              </Typography>
            </Box>}
          {!hideActionButtons && <Box display="flex" gap={1} justifyContent="flex-end">
              {compact ? <>
                  <Tooltip title={t("content.mediaMoreOptions")} placement="bottom" enterDelay={800}>
                    <IconButton data-cy="mediaItemMenuButton" size="small" onClick={(event: any) => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
              }}>
                      <MoreHorizRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={(event: any) => {
              event.stopPropagation();
              setAnchorEl(null);
            }} slotProps={{
              paper: {
                style: {
                  width: "160px"
                }
              },
              list: {
                style: {
                  minWidth: "fit-content"
                }
              }
            }} anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }} transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }} sx={{
              "& .MuiPaper-root .MuiList-root .MuiButtonBase-root": {
                "& .MuiListItemIcon-root svg": {
                  fill: "action.inactive"
                },
                "& .MuiListItemText-root .MuiTypography-root": {
                  fontWeight: 500,
                  color: "text.secondary"
                }
              }
            }}>
                    {!isURL && data && <MenuItem dense data-cy="mediaMenuItem-editFile" onClick={event => {
                event.stopPropagation();
                setAnchorEl(null);
                onPreview && onPreview(imageZUID);
              }}>
                        <ListItemIcon>
                          <EditRounded />
                        </ListItemIcon>
                        <ListItemText>
                          {t("content.mediaEditFile")}
                        </ListItemText>
                      </MenuItem>}
                    {(!isBynderAsset || isBynderAsset && isBynderSessionValid) && <MenuItem dense data-cy="mediaMenuItem-swapFile" onClick={event => {
                event.stopPropagation();
                setAnchorEl(null);
                onReplace && onReplace(imageZUID);
              }}>
                        <ListItemIcon>
                          <ImageSync />
                        </ListItemIcon>
                        <ListItemText>
                          {t("content.mediaSwapFile")}
                        </ListItemText>
                      </MenuItem>}
                    {!isURL && !isBynderAsset && data && <MenuItem dense data-cy="mediaMenuItem-renameFile" onClick={event => {
                event.stopPropagation();
                setAnchorEl(null);
                setShowRenameFileModal(true);
              }}>
                        <ListItemIcon>
                          <DriveFileRenameOutlineRounded />
                        </ListItemIcon>
                        <ListItemText>
                          {t("content.mediaRenameFile")}
                        </ListItemText>
                      </MenuItem>}

                    {!isURL && !isBynderAsset && data && <MenuItem dense data-cy="mediaMenuItem-copyZUID" onClick={event => {
                event.stopPropagation();
                handleCopyClick(imageZUID, true);
              }}>
                        <ListItemIcon>
                          {isCopiedZuid ? <CheckRounded /> : <WidgetsRounded />}
                        </ListItemIcon>
                        <ListItemText>
                          {t("content.mediaCopyZuid")}
                        </ListItemText>
                      </MenuItem>}
                    {data && <MenuItem dense data-cy="mediaMenuItem-copyFileURL" onClick={event => {
                event.stopPropagation();
                handleCopyClick(isURL ? imageZUID : data?.url, false);
              }}>
                        <ListItemIcon>
                          {isCopied ? <CheckRounded /> : <LinkRounded />}
                        </ListItemIcon>
                        <ListItemText>
                          {t("content.mediaCopyFileUrl")}
                        </ListItemText>
                      </MenuItem>}
                    <MenuItem dense data-cy="mediaMenuItem-removeFile" onClick={event => {
                event.stopPropagation();
                setAnchorEl(null);
                onRemove && onRemove(imageZUID);
              }}>
                      <ListItemIcon>
                        <CloseRounded />
                      </ListItemIcon>
                      <ListItemText>
                        {t("content.mediaRemoveFile")}
                      </ListItemText>
                    </MenuItem>
                  </Menu>
                </> : <>
                  {!isBynderAsset || isBynderAsset && isBynderSessionValid ? <Tooltip title={t("content.mediaSwapFile")} placement="bottom" enterDelay={800}>
                      <IconButton size="small" onClick={(event: any) => {
                event.stopPropagation();
                onReplace && onReplace(imageZUID);
              }}>
                        <ImageSync fontSize="small" />
                      </IconButton>
                    </Tooltip> : <></>}
                  {!isURL && data && <Tooltip title={t("content.mediaEditFile")} placement="bottom" enterDelay={800}>
                      <IconButton size="small" onClick={(event: any) => {
                event.stopPropagation();
                onPreview && onPreview(imageZUID);
              }}>
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>}
                  <Tooltip title={t("content.mediaMoreOptions")} placement="bottom" enterDelay={800}>
                    <IconButton size="small" onClick={(event: any) => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
              }}>
                      <MoreHorizRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={(event: any) => {
              event.stopPropagation();
              setAnchorEl(null);
            }} slotProps={{
              paper: {
                style: {
                  width: "288px"
                }
              }
            }} anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }} transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}>
                    {!isURL && !isBynderAsset && data && <>
                        <MenuItem onClick={event => {
                  event.stopPropagation();
                  setAnchorEl(null);
                  setShowRenameFileModal(true);
                }}>
                          <ListItemIcon>
                            <DriveFileRenameOutlineRounded />
                          </ListItemIcon>
                          <ListItemText>
                            {t("content.mediaRename")}
                          </ListItemText>
                        </MenuItem>
                        <MenuItem onClick={event => {
                  event.stopPropagation();
                  setAnchorEl(null);
                  setIsReplaceFileModalOpen(true);
                }}>
                          <ListItemIcon>
                            <FileReplace />
                          </ListItemIcon>
                          <ListItemText>
                            {t("content.mediaReplaceFile")}
                          </ListItemText>
                        </MenuItem>
                        <MenuItem onClick={event => {
                  event.stopPropagation();
                  handleCopyClick(imageZUID, true);
                }}>
                          <ListItemIcon>
                            {isCopiedZuid ? <CheckRounded /> : <WidgetsRounded />}
                          </ListItemIcon>
                          <ListItemText>
                            {t("content.mediaCopyZuid")}
                          </ListItemText>
                        </MenuItem>
                      </>}
                    {data && <MenuItem onClick={event => {
                event.stopPropagation();
                handleCopyClick(isURL ? imageZUID : data?.url, false);
              }}>
                        <ListItemIcon>
                          {isCopied ? <CheckRounded /> : <LinkRounded />}
                        </ListItemIcon>
                        <ListItemText>
                          {t("content.mediaCopyFileUrl")}
                        </ListItemText>
                      </MenuItem>}
                    <MenuItem onClick={event => {
                event.stopPropagation();
                setAnchorEl(null);
                onRemove && onRemove(imageZUID);
              }}>
                      <ListItemIcon>
                        <CloseRounded />
                      </ListItemIcon>
                      <ListItemText>{t("common.remove")}</ListItemText>
                    </MenuItem>
                  </Menu>
                </>}
            </Box>}
        </Box>
      </Box>
      {showRenameFileModal && <RenameFileModal src={data.url} handleUpdateMutation={handleUpdateMutation} onSetNewFilename={setNewFilename} onClose={() => setShowRenameFileModal(false)} newFilename={newFilename} isSuccessUpdate={isSuccessUpdate} isLoadingUpdate={isLoadingUpdate} resetUpdate={resetUpdate} extension={fileExtension(data.filename)} />}
      {isReplaceFileModalOpen && <ReplaceFileModal originalFile={data} onClose={() => setIsReplaceFileModalOpen(false)} onCancel={() => setIsReplaceFileModalOpen(false)} />}
    </Box>;
};
FieldTypeMedia.displayName = "FieldTypeMedia";